import { createFileRoute } from '@tanstack/react-router';
import { LegalPageLayout } from '@/components/legal/LegalPageLayout';

export const Route = createFileRoute('/legal/terms')({
  component: TermsOfService,
});

function TermsOfService() {
  return (
    <LegalPageLayout title="Terms of Service" lastUpdated="February 25, 2026">
      <p>
        These Terms of Service ("Terms") govern your use of Wundra ("Service"),
        operated by Grateful8 Consulting LLC ("we," "us," or "our"). By
        accessing or using the Service, you agree to be bound by these Terms.
      </p>

      <h2>1. Acceptance of Terms</h2>
      <p>
        By creating an account or using Wundra, you confirm that you are at
        least 13 years old and agree to these Terms. If you do not agree, you
        may not use the Service.
      </p>

      <h2>2. Service Description</h2>
      <p>
        Wundra is an AI assisted travel planning application that helps users
        create personalized trip itineraries. The Service uses artificial
        intelligence, third-party data sources, and user preferences to generate
        travel recommendations, itineraries, and related content.
      </p>

      <h2>3. Account Registration</h2>
      <p>
        You may register using email/password, Google, or Apple sign-in. You are
        responsible for maintaining the security of your account credentials and
        for all activity under your account. You agree to provide accurate
        information and to update it as necessary.
      </p>

      <h2>4. Payment Terms</h2>
      <p>
        Certain features of Wundra require a one-time payment processed through
        Stripe. All purchases are final unless otherwise required by applicable
        law. Prices are displayed at the time of purchase and include any
        applicable fees. We reserve the right to change pricing at any time for
        future purchases.
      </p>

      <h2>5. User Content</h2>
      <p>
        You retain ownership of content you create within Wundra, including trip
        notes, preferences, and customizations. By using the Service, you grant
        us a limited license to store, process, and display your content as
        necessary to provide the Service.
      </p>

      <h2>6. AI-Generated Content</h2>
      <p>
        Wundra uses OpenAI and other AI services to generate itinerary
        suggestions, activity recommendations, and travel content. You
        acknowledge that:
      </p>
      <ul>
        <li>
          AI-generated content is provided for informational and planning
          purposes only
        </li>
        <li>
          Recommendations may contain inaccuracies regarding hours, pricing,
          availability, or other details
        </li>
        <li>
          You are responsible for independently verifying travel information
          before making bookings or travel decisions
        </li>
        <li>
          We do not guarantee the accuracy, completeness, or suitability of any
          AI-generated recommendation
        </li>
      </ul>

      <h2>7. Third-Party Services</h2>
      <p>
        Wundra integrates with third-party services including Google Maps,
        Stripe, TripAdvisor, Foursquare, and AeroDataBox. Your use of these
        services through Wundra is subject to their respective terms and privacy
        policies. We are not responsible for the availability, accuracy, or
        content provided by third-party services.
      </p>

      <h2>8. Intellectual Property</h2>
      <p>
        The Wundra name, logo, design, and underlying technology are the
        property of Grateful8 Consulting LLC. You may not copy, modify,
        distribute, or create derivative works from any part of the Service
        without our prior written consent.
      </p>

      <h2>9. Prohibited Conduct</h2>
      <p>You agree not to:</p>
      <ul>
        <li>Use the Service for any unlawful purpose</li>
        <li>
          Attempt to reverse engineer, decompile, or extract source code from
          the Service
        </li>
        <li>
          Interfere with or disrupt the Service or its infrastructure
        </li>
        <li>
          Scrape, harvest, or collect data from the Service through automated
          means
        </li>
        <li>
          Impersonate another person or misrepresent your affiliation with any
          entity
        </li>
        <li>
          Use the Service to generate content that is harmful, abusive, or
          violates the rights of others
        </li>
      </ul>

      <h2>10. Limitation of Liability</h2>
      <p>
        To the fullest extent permitted by law, Grateful8 Consulting LLC shall
        not be liable for any indirect, incidental, special, consequential, or
        punitive damages arising from your use of the Service. Travel
        recommendations provided by Wundra are informational only and do not
        constitute professional travel advice. You assume all risk associated
        with travel decisions made based on information from the Service.
      </p>
      <p>
        Our total liability for any claims arising from or related to the
        Service shall not exceed the amount you paid for the Service in the
        twelve months preceding the claim.
      </p>

      <h2>11. Termination</h2>
      <p>
        We may suspend or terminate your access to the Service at any time for
        violation of these Terms or for any other reason at our discretion. You
        may delete your account at any time through your profile settings. Upon
        termination, your right to use the Service ceases immediately, though we
        may retain certain data as required by law.
      </p>

      <h2>12. Governing Law</h2>
      <p>
        These Terms are governed by and construed in accordance with the laws of
        the State of Ohio, United States, without regard to conflict of law
        principles. Any disputes shall be resolved in the courts located in
        Ohio.
      </p>

      <h2>13. Changes to These Terms</h2>
      <p>
        We reserve the right to modify these Terms at any time. We will notify
        you of material changes by posting the updated Terms on this page. Your
        continued use of the Service after changes constitutes acceptance of the
        revised Terms.
      </p>

      <h2>14. Contact Us</h2>
      <p>
        If you have questions about these Terms, contact us at:
      </p>
      <ul>
        <li>Grateful8 Consulting LLC</li>
        <li>
          Email: <a href="mailto:hello@wundra.app">hello@wundra.app</a>
        </li>
      </ul>
    </LegalPageLayout>
  );
}
